import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight, Home } from "lucide-react";

export default function PortalDeudaPage() {
  const { token, API } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [deuda, setDeuda] = useState({ total_pendiente: 0, inmuebles: [] });
  const navigate = useNavigate();

  useEffect(() => { axios.get(`${API}/liquidaciones/mi-deuda`, auth).then(r => setDeuda(r.data)); }, []);

  const fmt = n => `Bs. ${(Number(n)||0).toLocaleString("es-VE", { maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="portal-deuda-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-orange-500" />Mi Deuda
          </h1>
          <p className="text-green-700/70 mt-1">Detalle de liquidaciones pendientes por inmueble</p>
        </div>
        {deuda.total_pendiente > 0 && (
          <Button data-testid="btn-ir-pagar" onClick={() => navigate("/portal/reportar-pago")} className="bg-orange-500 hover:bg-orange-600 text-white">
            Reportar pago <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      <Card className="p-6 border-0 text-white" style={{ background: "linear-gradient(135deg,#14532D,#16A34A)" }}>
        <p className="text-orange-300 text-xs font-bold uppercase tracking-wider">Total deuda actual</p>
        <p className="font-display font-extrabold text-4xl mt-2">{fmt(deuda.total_pendiente)}</p>
        <p className="text-sm text-white/80 mt-1">{deuda.total_liquidaciones || 0} liquidación(es) pendiente(s)</p>
      </Card>

      {deuda.inmuebles?.map((b, idx) => (
        <Card key={idx} className="p-5 border-green-100" data-testid={`inmueble-deuda-${idx}`}>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Home className="w-5 h-5 text-green-700" />
              <div>
                <p className="font-display font-bold text-green-900">{b.inmueble.direccion}</p>
                <p className="text-xs text-green-700/70 capitalize">{b.inmueble.tipo_zona} · {b.inmueble.metros_cuadrados} m² · {b.inmueble.actividad_especifica || "sin clasificación"}</p>
              </div>
            </div>
            <p className="font-display font-bold text-xl text-orange-600">{fmt(b.subtotal)}</p>
          </div>

          {b.liquidaciones_pendientes.length === 0 ? (
            <p className="text-center text-green-700 py-4">¡Sin deudas para este inmueble! 🎉</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-green-50 text-green-900"><tr>
                  <th className="text-left p-2.5">Período</th><th className="text-left p-2.5">Planilla</th><th className="text-left p-2.5">Concepto</th><th className="text-right p-2.5">Monto</th>
                </tr></thead>
                <tbody>{b.liquidaciones_pendientes.map(l => (
                  <tr key={l.id} className="border-b border-green-50">
                    <td className="p-2.5 font-mono">{l.periodo}</td>
                    <td className="p-2.5 font-mono text-xs">{l.nro_planilla||"-"}</td>
                    <td className="p-2.5">{l.descripcion}</td>
                    <td className="p-2.5 text-right font-semibold">{fmt(l.monto)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </Card>
      ))}

      {deuda.declaraciones_pendientes && deuda.declaraciones_pendientes.length > 0 && (
        <Card className="p-5 border-green-100" data-testid="declaraciones-pendientes">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            <p className="font-display font-bold text-green-900">Planillas de cobro pendientes</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-green-50 text-green-900"><tr>
                <th className="text-left p-2.5">Planilla</th>
                <th className="text-left p-2.5">Período</th>
                <th className="text-left p-2.5">Concepto</th>
                <th className="text-right p-2.5">Monto</th>
              </tr></thead>
              <tbody>{deuda.declaraciones_pendientes.map(d => (
                <tr key={d.id} className="border-b border-green-50" data-testid={`decl-row-${d.nro_planilla}`}>
                  <td className="p-2.5 font-mono text-xs">{d.nro_planilla || "-"}</td>
                  <td className="p-2.5 font-mono">{d.periodo}</td>
                  <td className="p-2.5">{d.descripcion}</td>
                  <td className="p-2.5 text-right font-semibold">{fmt(d.monto)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}

      {(!deuda.inmuebles || deuda.inmuebles.length === 0) && (!deuda.declaraciones_pendientes || deuda.declaraciones_pendientes.length === 0) && (
        <Card className="p-8 text-center border-green-100">
          <p className="text-slate-500">¡Sin deudas pendientes en este momento! 🎉</p>
        </Card>
      )}
    </div>
  );
}
